import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import client from "@/api/openapi-ts";
import type { components } from "@/api/schema.d.ts";
import {
  defaultAppClientPermissionCustomizationFlags,
  isLiveAppClientUser,
} from "@/helpers/userPermissions";

type AppClient = components["schemas"]["AppClient"];
type AppClientCreateRequest = components["schemas"]["AppClientCreateRequest"];
type AppClientUpdateRequest = components["schemas"]["AppClientUpdateRequest"];

/** In-memory / new-client form state; tier and theme unset until the user selects them. */
export type AppClientEditorModel = Omit<
  AppClient,
  "id" | "app_pricing_tier_id" | "theme_id" | "app_pricing_tier" | "theme"
> & {
  id?: number | undefined;
  app_pricing_tier_id: number | undefined;
  theme_id: number | undefined;
  app_pricing_tier?: AppClient["app_pricing_tier"];
  theme?: AppClient["theme"];
};

export type AppClientEditable = AppClient | AppClientEditorModel;

type AppClientRequestPayload =
  | AppClientEditable
  | AppClientCreateRequest
  | AppClientUpdateRequest;

type AssociationIdsKey = keyof Pick<
  AppClientCreateRequest,
  "tableau_workbook_ids" | "account_manager_ids" | "contact_ids" | "user_ids"
>;

/** Vuetify multi-selects may bind `id[]` or full `{ id }` rows; normalize for API + dirty checks. */
export function mixedToSortedIds(
  items: readonly (number | { id: number })[] | undefined,
): number[] {
  if (!items?.length) return [];
  return [...items]
    .map((x) => (typeof x === "number" ? x : x.id))
    .filter((id): id is number => typeof id === "number")
    .sort((a, b) => a - b);
}

function associationIdsForRequest(
  payload: AppClientRequestPayload,
  idsKey: AssociationIdsKey,
  objectsKey: keyof Pick<
    AppClient,
    "tableau_workbooks" | "account_managers" | "contacts" | "users"
  >,
): number[] {
  const fromRequest = (payload as Partial<AppClientCreateRequest>)[idsKey];
  if (Array.isArray(fromRequest)) {
    return [...fromRequest].sort((a, b) => a - b);
  }
  const objects = (payload as AppClient)[objectsKey];
  return mixedToSortedIds(
    objects as readonly (number | { id: number })[] | undefined,
  );
}

/** Shared fields for POST and PATCH bodies. */
function toAppClientWriteBodyBase(
  payload: AppClientRequestPayload,
): Omit<AppClientCreateRequest, "user_ids"> {
  const tierId = payload.app_pricing_tier_id;
  const themeId = payload.theme_id;
  if (tierId == null || themeId == null) {
    throw new Error("Default tier and theme are required to save");
  }

  const geographies =
    "geographies" in payload ? payload.geographies : undefined;

  const rawExpires = payload.access_expires_at as
    | string
    | Date
    | null
    | undefined;
  const access_expires_at: string | null =
    rawExpires === undefined || rawExpires === null
      ? null
      : rawExpires instanceof Date
        ? rawExpires.toISOString()
        : rawExpires;

  return {
    name: payload.name,
    app_pricing_tier_id: tierId,
    theme_id: themeId,
    type: payload.type,
    access_expires_at,
    products: payload.products ?? [],
    meta_audience_categories: payload.meta_audience_categories ?? [],
    meta_audiences: payload.meta_audiences ?? [],
    geographies: geographies ?? [],
    tableau_workbook_ids: associationIdsForRequest(
      payload,
      "tableau_workbook_ids",
      "tableau_workbooks",
    ),
    account_manager_ids: associationIdsForRequest(
      payload,
      "account_manager_ids",
      "account_managers",
    ),
    contact_ids: associationIdsForRequest(payload, "contact_ids", "contacts"),
  };
}

function toAppClientCreateRequest(
  payload: AppClientEditable | AppClientCreateRequest,
): AppClientCreateRequest {
  return {
    ...toAppClientWriteBodyBase(payload),
    user_ids: associationIdsForRequest(payload, "user_ids", "users"),
  };
}

function toAppClientUpdateRequest(
  payload: AppClientEditable | AppClientUpdateRequest,
): AppClientUpdateRequest {
  return toAppClientWriteBodyBase(payload);
}

export const useAppClientStore = defineStore("appClient", () => {
  const loadingClient = ref(false);
  const loadingClients = ref(false);
  const loadingUser = ref(false);
  const loadingUsers = ref(false);
  const loadingMetaAudienceCategories = ref(false);
  const saving = ref(false);
  const deleting = ref(false);
  const addingUsers = ref(0);
  const deletingUser = ref(0);
  const migratingCampaignsAndSurveys = ref(false);
  const appClient = ref<AppClientEditable | undefined>(undefined);
  const appClients = ref<components["schemas"]["AppClient"][]>([]);
  const metaAudienceCategories = ref<
    components["schemas"]["AppMetaAudienceCategory"][]
  >([]);

  /** Bumps on each fetch or explicit cancel; only the latest id may update `metaAudienceCategories`. */
  let metaAudienceCategoriesFetchId = 0;

  return {
    loadingClient,
    loadingClients,
    loadingUser,
    loadingUsers,
    loadingMetaAudienceCategories,
    saving,
    deleting,
    addingUsers,
    deletingUser,
    migratingCampaignsAndSurveys,
    appClient,
    appClients,
    metaAudienceCategories,

    async index() {
      loadingClients.value = true;
      try {
        const { data } = await client.GET("/api/v1/app/clients");
        appClients.value = data as components["schemas"]["AppClient"][];
      } catch (error) {
        console.error(error);
      } finally {
        loadingClients.value = false;
      }
    },

    async get(appClientId: number) {
      loadingClient.value = true;
      try {
        const { data } = await client.GET(
          "/api/v1/app/clients/{app_client_id}",
          {
            params: {
              path: {
                app_client_id: appClientId,
              },
            },
          },
        );
        appClient.value = data as components["schemas"]["AppClient"];
        if (appClient.value?.users) {
          appClient.value.users =
            appClient.value.users.filter(isLiveAppClientUser);
        }
      } catch (error) {
        console.error(error);
        appClient.value = undefined;
      } finally {
        loadingClient.value = false;
      }
    },

    async create(payload: AppClientEditable | AppClientCreateRequest) {
      const body = toAppClientCreateRequest(payload);
      saving.value = true;
      try {
        const { data, response } = await client.POST("/api/v1/app/clients", {
          body,
        });
        if (response.status === 200) {
          const created = data as AppClient;
          created.users_count = created.users?.length ?? 0;
          appClients.value.push(created);
          return created;
        }
        throw new Error(response.statusText);
      } finally {
        saving.value = false;
      }
    },

    async update(
      appClientId: number,
      payload: AppClientEditable | AppClientUpdateRequest,
    ): Promise<components["schemas"]["AppClient"] | undefined> {
      // `AppClientUpdateRequest` has no `user_ids`; membership uses POST/DELETE `/users`.
      const body = toAppClientUpdateRequest(payload);
      saving.value = true;
      try {
        const { data, response } = await client.PATCH(
          "/api/v1/app/clients/{app_client_id}",
          {
            params: { path: { app_client_id: appClientId } },
            body,
          },
        );
        if (response.status === 200) {
          const updated = data as components["schemas"]["AppClient"];
          appClients.value = appClients.value.map((c) =>
            c.id === appClientId ? updated : c,
          );
          const current = appClient.value;
          if (current?.id === appClientId) {
            current.access_expires_at = updated.access_expires_at ?? null;
          }
          return updated;
        }
        throw new Error(response.statusText);
      } finally {
        saving.value = false;
      }
    },

    async delete(appClientId: number) {
      deleting.value = true;
      try {
        const { response } = await client.DELETE(
          "/api/v1/app/clients/{app_client_id}",
          {
            params: { path: { app_client_id: appClientId } },
            // Endpoint returns an empty 200 body; parse as text so openapi-fetch
            // does not throw trying to JSON-parse an empty response.
            parseAs: "text",
          },
        );
        if (response.status === 200) {
          appClients.value = appClients.value.filter(
            (client) => client.id !== appClientId,
          );
        } else {
          throw new Error(response.statusText);
        }
      } finally {
        deleting.value = false;
      }
    },

    async listUsers(appClientId: number) {
      loadingUsers.value = true;
      try {
        const { data, response } = await client.GET(
          "/api/v1/app/clients/{app_client_id}/users",
          {
            params: { path: { app_client_id: appClientId } },
          },
        );
        if (response.status === 200) {
          return data as components["schemas"]["AppClientUser"][];
        }
        throw new Error(response.statusText);
      } finally {
        loadingUsers.value = false;
      }
    },

    async getUser(appClientId: number, userId: number) {
      loadingUser.value = true;
      try {
        const { data, response } = await client.GET(
          "/api/v1/app/clients/{app_client_id}/users/{user_id}",
          {
            params: { path: { app_client_id: appClientId, user_id: userId } },
          },
        );
        if (response.status === 200) {
          return data as unknown as components["schemas"]["UserProfile"];
        }
        throw new Error(response.statusText);
      } catch (error) {
        console.error(error);
        return undefined;
      } finally {
        loadingUser.value = false;
      }
    },

    async addUsers(
      appClientId: number,
      users: components["schemas"]["UserProfile"][],
    ) {
      addingUsers.value++;
      try {
        const liveUsers = users.filter(isLiveAppClientUser);
        const { response } = await client.POST(
          "/api/v1/app/clients/{app_client_id}/users",
          {
            params: { path: { app_client_id: appClientId } },
            body: { user_ids: liveUsers.map((user) => user.id) },
          },
        );
        if (response.ok) {
          const current = appClient.value;
          const clientInList = appClients.value.find(
            (c) => c.id === appClientId,
          );

          if (current?.id === appClientId && current.users) {
            const existingIds = new Set(current.users.map((u) => u.id));
            const newUsers = liveUsers
              .filter((user) => !existingIds.has(user.id))
              .map((user) => ({
                id: user.id,
                first_name: user.first_name,
                last_name: user.last_name,
                name: user.name,
                email: user.email,
                active: user.active ?? true,
                deleted_at: user.deleted_at ?? null,
                roles: user.roles,
                products: user.products ?? [],
                ...defaultAppClientPermissionCustomizationFlags,
              }));
            current.users.push(...newUsers);
            if (clientInList) {
              clientInList.users_count = current.users.length;
            }
          } else if (clientInList) {
            // Client detail is not loaded (e.g. global Users tab) — still keep sidebar counts in sync.
            const listMembers = clientInList.users;
            const existingIds = new Set((listMembers ?? []).map((u) => u.id));
            const added = liveUsers.filter(
              (u) => !existingIds.has(u.id),
            ).length;
            if (added > 0) {
              clientInList.users_count =
                (clientInList.users_count ?? 0) + added;
            }
          }
        } else {
          throw new Error(response.statusText);
        }
      } finally {
        addingUsers.value--;
      }
    },

    async removeUser(appClientId: number, userId: number) {
      deletingUser.value++;
      try {
        const { response } = await client.DELETE(
          "/api/v1/app/clients/{app_client_id}/users/{user_id}",
          {
            params: { path: { app_client_id: appClientId, user_id: userId } },
            // Endpoint returns an empty 200 body; parse as text so openapi-fetch
            // does not throw trying to JSON-parse an empty response.
            parseAs: "text",
          },
        );
        if (response.status === 200) {
          const current = appClient.value;
          const clientInList = appClients.value.find(
            (c) => c.id === appClientId,
          );

          if (current?.id === appClientId && current.users) {
            current.users = current.users.filter((user) => user.id !== userId);
            if (clientInList) {
              clientInList.users_count = current.users.length;
              if (clientInList.users?.length) {
                clientInList.users = clientInList.users.filter(
                  (u) => u.id !== userId,
                );
              }
            }
          } else if (clientInList) {
            const members = clientInList.users;
            const prevCount = clientInList.users_count ?? members?.length ?? 0;
            if (members?.length) {
              clientInList.users = members.filter((u) => u.id !== userId);
            }
            clientInList.users_count = Math.max(0, prevCount - 1);
          }
        } else {
          throw new Error(response.statusText);
        }
      } finally {
        deletingUser.value--;
      }
    },

    /** Invalidate in-flight meta audience category loads (e.g. client detail closed). */
    cancelMetaAudienceCategoriesLoad() {
      metaAudienceCategoriesFetchId++;
      metaAudienceCategories.value = [];
      loadingMetaAudienceCategories.value = false;
    },

    async getMetaAudienceCategories(appClientId: number) {
      const fetchId = ++metaAudienceCategoriesFetchId;
      loadingMetaAudienceCategories.value = true;
      try {
        const { data, response } = await client.GET(
          "/api/v1/app/clients/{app_client_id}/meta_audience_categories",
          {
            params: { path: { app_client_id: appClientId } },
          },
        );
        if (fetchId !== metaAudienceCategoriesFetchId) return;
        if (response.status === 200) {
          metaAudienceCategories.value =
            data as components["schemas"]["AppMetaAudienceCategory"][];
        }
      } catch (error) {
        console.error(error);
        if (fetchId === metaAudienceCategoriesFetchId) {
          metaAudienceCategories.value = [];
        }
      } finally {
        if (fetchId === metaAudienceCategoriesFetchId) {
          loadingMetaAudienceCategories.value = false;
        }
      }
    },

    async migrateCampaignsAndSurveys(orgId: number, appClientId: number) {
      migratingCampaignsAndSurveys.value = true;
      try {
        const { response } = await client.POST(
          "/api/v1/app/clients/migrate_campaigns_and_surveys",
          {
            body: { org_id: orgId, app_client_id: appClientId },
          },
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
      } finally {
        migratingCampaignsAndSurveys.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppClientStore, import.meta.hot));
}
